import {
    createStyles,
    IconButton,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    SwipeableDrawer,
    WithStyles,
    withStyles,
} from "@material-ui/core";
import {
    CloseIcon,
    CopyIcon,
    Divider,
    EditIcon,
    EllipsisVerticalIcon,
    NewMessageIcon,
    PauseIcon,
    PlayIcon,
    RelodeTheme,
    ShareAltIcon,
} from "@relode/core";
import { JobStatus } from "@relode/core/domain";
import * as React from "react";

const styles = (theme: RelodeTheme) =>
    createStyles({
        mobileOptions: {
            position: "absolute",
            right: 0,
            top: 0,
            marginRight: theme.spacing(2),
        },
    });

interface Props extends WithStyles<typeof styles> {
    availableJobStatuses: JobStatus[];
    jobStatusAction: (status: JobStatus) => void;
    shareAction?: (boolean) => void;
    messageAction?: (boolean) => void;
    jobCopyAction?: () => void;
    jobEditAction?: () => void;
}

export const JobActionsMobile = withStyles(styles, {
    name: "JobActionsMobile",
})((props: Props) => {
    const {
        classes,
        jobStatusAction,
        availableJobStatuses,
        shareAction,
        messageAction,
        jobCopyAction,
        jobEditAction,
    } = props;
    const [drawerIsOpen, openDrawer] = React.useState(() => false);

    //Material's recommends checking for iOS users when using their SwipeableDrawer
    //The following properties are used in this documentation website for optimal usability of the component: https://material-ui.com/components/drawers/
    //iOS is hosted on high-end devices. The backdrop transition can be enabled without dropping frames. The performance will be good enough.
    //iOS has a "swipe to go back" feature that interferes with the discovery feature, so discovery has to be disabled.

    const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent);

    const actionIcon = (status: JobStatus, mobile: boolean) => {
        switch (status) {
            case JobStatus.Closed: {
                return (
                    <CloseIcon
                        fontSize={mobile ? "small" : undefined}
                        color="primary"
                    />
                );
            }
            case JobStatus.Paused: {
                return (
                    <PauseIcon
                        fontSize={mobile ? "small" : undefined}
                        color="primary"
                    />
                );
            }
            case JobStatus.Open: {
                return (
                    <PlayIcon
                        fontSize={mobile ? "small" : undefined}
                        color="primary"
                    />
                );
            }
        }
    };

    const hasActions =
        !!messageAction ||
        !!shareAction ||
        !!jobCopyAction ||
        !!jobEditAction ||
        (!!availableJobStatuses &&
            availableJobStatuses.length > 0 &&
            !!jobStatusAction);

    if (!hasActions) return null;

    return (
        <div className={classes.mobileOptions}>
            <IconButton
                onClick={() => openDrawer(true)}
                edge="end"
                id="mobileOptions"
                color="primary"
                title="open more action options"
            >
                <EllipsisVerticalIcon />
            </IconButton>
            <SwipeableDrawer
                anchor="bottom"
                open={drawerIsOpen}
                onOpen={() => openDrawer(true)}
                onClose={() => openDrawer(false)}
                disableBackdropTransition={!iOS}
                disableDiscovery={true}
            >
                <div
                    role="presentation"
                    onClick={() => openDrawer(false)}
                    onKeyDown={() => openDrawer(false)}
                >
                    <List>
                        {!!messageAction ? (
                            <ListItem
                                button
                                onClick={() => messageAction(true)}
                            >
                                <ListItemIcon>
                                    <NewMessageIcon color="primary" />
                                </ListItemIcon>
                                <ListItemText primary="send a message" />
                            </ListItem>
                        ) : null}
                        {!!shareAction ? (
                            <ListItem button onClick={() => shareAction(true)}>
                                <ListItemIcon>
                                    <ShareAltIcon color="primary" />
                                </ListItemIcon>
                                <ListItemText primary="share this job" />
                            </ListItem>
                        ) : null}
                        {!!jobCopyAction ? (
                            <ListItem button onClick={() => jobCopyAction()}>
                                <ListItemIcon>
                                    <CopyIcon color="primary" />
                                </ListItemIcon>
                                <ListItemText primary="copy job" />
                            </ListItem>
                        ) : null}
                        {!!jobEditAction ? (
                            <ListItem button onClick={() => jobEditAction()}>
                                <ListItemIcon>
                                    <EditIcon color="primary" />
                                </ListItemIcon>
                                <ListItemText primary="edit job" />
                            </ListItem>
                        ) : null}

                        {!!messageAction ||
                        !!shareAction ||
                        !!jobCopyAction ||
                        !!jobEditAction ? (
                            <Divider />
                        ) : null}

                        {availableJobStatuses.map((status, i) => (
                            <ListItem
                                key={i}
                                button
                                onClick={() => jobStatusAction(status)}
                            >
                                <ListItemIcon>
                                    {actionIcon(status, true)}
                                </ListItemIcon>
                                <ListItemText
                                    primary={`${status.actionLabel}`}
                                />
                            </ListItem>
                        ))}
                    </List>
                    <Divider />
                </div>
            </SwipeableDrawer>
        </div>
    );
});
